import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useEffect,
} from "react";
import { useAuth } from "./AuthContext";
import { getSessionId } from "../api/visualizeService";
import { visualizeService } from "../api/visualizeService";
import { useTheme } from "./ThemeContext";
import { imageFocusParams } from "../utils/boundingBox";

const VisualizerContext = createContext(null);

/**
 * Provider for visualizer: session identity (userId, anchorId, sessionId) and
 * session-scoped commands (zoomIn, focus, clearTool, etc.). Commands use the
 * provider's session internally; callers never pass userId/session routing keys.
 * Must be used inside AuthProvider. anchorId is the TOC tab anchor (compiler/stepsApi only).
 */
export const VisualizerProvider = ({ children, anchorId = null }) => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const userId = user?.id ?? null;
  const sessionId = useMemo(() => getSessionId(userId), [userId]);

  const sendJrpc = useCallback(
    (method, params = {}) =>
      visualizeService.sendUnityJrpc(method, params, userId),
    [userId],
  );

  const zoomIn = useCallback(() => sendJrpc("Zoom.in", {}), [sendJrpc]);
  const zoomOut = useCallback(() => sendJrpc("Zoom.out", {}), [sendJrpc]);
  const clearTool = useCallback(() => sendJrpc("Tool.clear", {}), [sendJrpc]);
  const toolPan = useCallback(() => sendJrpc("Tool.pan", {}), [sendJrpc]);
  const toolOrbit = useCallback(() => sendJrpc("Tool.orbit", {}), [sendJrpc]);
  const toolMove = useCallback(() => sendJrpc("Tool.move", {}), [sendJrpc]);
  const toolRotate = useCallback(() => sendJrpc("Tool.rotate", {}), [sendJrpc]);
  const focus = useCallback(() => sendJrpc("Part.focus", {}), [sendJrpc]);
  const unfocus = useCallback(() => sendJrpc("Part.unfocus", {}), [sendJrpc]);
  const showFocusRectangle = useCallback(
    () => sendJrpc("Image.rectangle.show", {}),
    [sendJrpc],
  );
  const hideFocusRectangle = useCallback(
    () => sendJrpc("Image.rectangle.hide", {}),
    [sendJrpc],
  );
  const resetSettings = useCallback(
    () => sendJrpc("Settings.reset", {}),
    [sendJrpc],
  );
  const togglePip = useCallback(
    () => sendJrpc("Camera.pip-toggle", {}),
    [sendJrpc],
  );
  const setBackgroundColor = useCallback(
    hex => sendJrpc("Settings.background", { hex }),
    [sendJrpc],
  );

  useEffect(() => {
    const bgColor = isDark ? "#1F2123" : "#F5F6F8";
    let cancelled = false;
    let timeout;
    let attempts = 0;

    const attempt = async () => {
      if (cancelled || attempts >= 20) return;
      attempts++;
      try {
        const res = await sendJrpc("Settings.background", { hex: bgColor });
        if (!cancelled && res?.error) {
          timeout = setTimeout(attempt, 8000);
        }
      } catch {
        if (!cancelled) timeout = setTimeout(attempt, 2000);
      }
    };

    attempt();
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [isDark, sendJrpc]);
  const showPart = useCallback(
    partId => sendJrpc("Part.show", { ids: [String(partId)] }),
    [sendJrpc],
  );
  const hidePart = useCallback(
    partId => sendJrpc("Part.hide", { ids: [String(partId)] }),
    [sendJrpc],
  );
  const showParts = useCallback(
    partIds => {
      const ids = Array.isArray(partIds) ? partIds : [partIds];
      const normalized = ids.map(id => String(id).trim()).filter(Boolean);
      if (normalized.length === 0) return;
      sendJrpc("Part.show", { ids: normalized });
    },
    [sendJrpc],
  );
  const hideParts = useCallback(
    partIds => {
      const ids = Array.isArray(partIds) ? partIds : [partIds];
      const normalized = ids.map(id => String(id).trim()).filter(Boolean);
      if (normalized.length === 0) return;
      sendJrpc("Part.hide", { ids: normalized });
    },
    [sendJrpc],
  );

  /** Sync Unity primary targets (Target.ids). No-op when empty to avoid Unity empty-param warning. */
  const setTargetIds = useCallback(
    partIds => {
      const ids = Array.isArray(partIds)
        ? partIds.map(id => String(id).trim()).filter(Boolean)
        : [];
      if (!ids.length) return;
      sendJrpc("Target.ids", { ids });
    },
    [sendJrpc],
  );

  const setSelectionParts = useCallback(
    partIds => {
      const ids = Array.isArray(partIds)
        ? partIds.map(id => String(id).trim()).filter(Boolean)
        : [];
      sendJrpc("Target.selection-ids", ids.length ? { ids } : {});
    },
    [sendJrpc],
  );
  const set2DImage = useCallback(
    imageUrl => {
      if (!imageUrl) return;
      sendJrpc("Fetch.image", { url: imageUrl });
    },
    [sendJrpc],
  );
  const clear2DImage = useCallback(() => sendJrpc("Fetch.clear2d", {}), [sendJrpc]);
  const clear3D = useCallback(() => sendJrpc("Fetch.clear3d", {}), [sendJrpc]);
  const show3D = useCallback(() => sendJrpc("Fetch.show3d", {}), [sendJrpc]);
  /** Focus 2D viewer on pixel bounds; omit coords to focus the full texture. */
  const focusImageArea = useCallback(
    (x_min, y_min, x_max, y_max) =>
      sendJrpc("Image.focus", imageFocusParams(x_min, y_min, x_max, y_max)),
    [sendJrpc],
  );

  const value = useMemo(
    () => ({
      userId,
      anchorId,
      sessionId,
      zoomIn,
      zoomOut,
      clearTool,
      toolPan,
      toolOrbit,
      toolMove,
      toolRotate,
      focus,
      unfocus,
      showFocusRectangle,
      hideFocusRectangle,
      resetSettings,
      togglePip,
      setBackgroundColor,
      showPart,
      hidePart,
      showParts,
      hideParts,
      setTargetIds,
      setSelectionParts,
      set2DImage,
      clear2DImage,
      clear3D,
      show3D,
      focusImageArea,
      sendUnityJrpc: sendJrpc,
    }),
    [
      userId,
      anchorId,
      sessionId,
      zoomIn,
      zoomOut,
      clearTool,
      toolPan,
      toolOrbit,
      toolMove,
      toolRotate,
      focus,
      unfocus,
      showFocusRectangle,
      hideFocusRectangle,
      resetSettings,
      togglePip,
      setBackgroundColor,
      showPart,
      hidePart,
      showParts,
      hideParts,
      setTargetIds,
      setSelectionParts,
      set2DImage,
      clear2DImage,
      clear3D,
      show3D,
      focusImageArea,
      sendJrpc,
    ],
  );

  return (
    <VisualizerContext.Provider value={value}>
      {children}
    </VisualizerContext.Provider>
  );
};

/**
 * Hook to read visualizer context: session (userId, anchorId, sessionId) and
 * session-scoped commands (zoomIn, zoomOut, focus, clearTool, etc.).
 * Command functions take no userId; they use the context's session internally.
 * `sendUnityJrpc(method, params)` forwards JSON-RPC notifications to Unity via A2A.
 */
export const useVisualizer = () => {
  const context = useContext(VisualizerContext);
  if (!context) {
    throw new Error("useVisualizer must be used within a VisualizerProvider");
  }
  return context;
};

/**
 * Hook to read only visualizer session (userId, anchorId, sessionId).
 * Use when a component needs session but not the command functions.
 */
export const useVisualizerSession = () => {
  const context = useContext(VisualizerContext);
  if (!context) {
    throw new Error(
      "useVisualizerSession must be used within a VisualizerProvider",
    );
  }
  return {
    userId: context.userId,
    anchorId: context.anchorId,
    sessionId: context.sessionId,
  };
};
